import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { getTranslation } from "common";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import { useTranslations } from "hooks";
import { SLA_START_TEXT_KEYS } from "model/slaStartModel";
import {
  SLA_SUGGESTION_TEXT_KEYS,
  SLA_SUGGESTION_UNAVAILABLE_KEY,
  isSlaSuggestionApplicable,
  isSlaSuggestionAvailable,
  slaSuggestionKey,
  type SlaSuggestion,
} from "model/slaSuggestionModel";

/**
 * Superficie delle **proposte** (piano Client sez. 5 F2c). Nessuna scrittura:
 * rende cio' che la sorgente propone e mette a disposizione un solo comando di
 * conferma esplicita. Le regole (quando una proposta e' applicabile, cosa fa
 * `applySlaSuggestion`) vivono in `model/slaSuggestionModel.ts`; qui c'e' solo
 * la resa.
 *
 * Garanzie F2c, e come sono ottenute:
 * - **mai applicata silenziosamente**: la proposta e' testo; la bozza cambia solo
 *   se il chiamante passa `onApply` e l'utente preme il comando di conferma. Il
 *   componente non ha effetti che scrivono, non deriva stato dalla proposta e non
 *   chiama alcuna API;
 * - **stima/cold-start dichiarati**: `estimable = false` rende un avviso con la
 *   chiave esistente `sla.validation.estimate_unavailable` e **nessun** istante:
 *   non si mostra una data che il Server non ha dichiarato;
 * - **errore della sorgente non bloccante**: `sourceError` e' un avviso della
 *   superficie. Il salvataggio dell'editor e l'avvio del processo non dipendono
 *   da questa sezione: nessuna proposta e' un prerequisito del flusso.
 *
 * Nessun comando di aggiornamento qui: la stima si rilegge quando cambia la
 * bozza (effetto con debounce di ogni superficie), quindi la sezione non aggiunge
 * un secondo pulsante di ricarica accanto a quelli gia' presenti.
 *
 * Chiavi riusate: `sla.start.preview.deadline` (etichetta del valore stimato),
 * `sla.start.preview.as_of` (istante della stima), `sla.start.loading`,
 * `sla.validation.estimate_unavailable` (stima non disponibile). Chiavi **nuove**:
 * `SLA_SUGGESTION_TEXT_KEYS` (conferma, marcatore, errore di sorgente), elencate
 * come mancanti nel riepilogo di F2c perche' il catalogo e' di Python.
 */
export interface SlaSuggestionsProps {
  /** Proposte correnti. Vuoto = nulla da proporre, nessun avviso. */
  suggestions: SlaSuggestion[];
  /** Revisione a schermo: una stima di un'altra revisione non e' applicabile. */
  modelRevision: number | null;
  /** Presente **solo** quando la superficie puo' davvero scrivere la proposta nella bozza. */
  onApply?: (suggestion: SlaSuggestion) => void;
  /** La bozza porta gia' il valore proposto: la superficie lo dichiara. */
  isApplied?: (suggestion: SlaSuggestion) => boolean;
  /** Sorgente non raggiungibile: si dichiara, non blocca (F2c). */
  sourceError?: boolean;
  loading?: boolean;
  /**
   * true quando il chiamante dichiara **lui** lo stato della stima: o il valore
   * (l'avvio, nel blocco `sla-start-preview-*`), o il motivo per cui non lo rende
   * (l'editor quando la timezone dell'organizzazione non e' leggibile). In quel
   * caso la sezione non ripete valore, istante e avviso di stima non
   * disponibile, e resta il solo gesto di conferma: nessun testo duplicato e
   * nessun avviso ripetuto due volte nella stessa area.
   */
  estimateRenderedByCaller?: boolean;
  /** Istante ISO -> etichetta localizzata, con la timezone dell'organizzazione. */
  formatDateTime: (iso: string) => string;
  /** Identificatore dei `data-testid` della sezione. */
  testIdPrefix: string;
}

const SlaSuggestions = ({
  suggestions = [],
  modelRevision,
  onApply,
  isApplied,
  sourceError = false,
  loading = false,
  estimateRenderedByCaller = false,
  formatDateTime,
  testIdPrefix,
}: SlaSuggestionsProps) => {
  const translations = useTranslations();

  /**
   * Il comando di conferma esiste solo dove esiste un campo di destinazione: se
   * il chiamante rende gia' la stima e non c'e' nulla da confermare, la sezione
   * non compare affatto (nessun riquadro vuoto, nessun titolo senza contenuto).
   */
  const hasApplyCommand =
    Boolean(onApply) &&
    (suggestions ?? []).some((suggestion) =>
      isSlaSuggestionApplicable(suggestion, modelRevision),
    );
  const hasContent = estimateRenderedByCaller
    ? hasApplyCommand
    : (suggestions ?? []).length > 0;

  if (!hasContent && !sourceError && !loading) return null;

  return (
    <Box
      data-testid={testIdPrefix}
      data-sla-source-error={sourceError ? "true" : "false"}
      sx={{
        p: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {getTranslation(translations, SLA_SUGGESTION_TEXT_KEYS.title)}
        </Typography>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
          <CircularProgress size={14} />
          <Typography variant="body2">
            {getTranslation(translations, SLA_START_TEXT_KEYS.loading)}
          </Typography>
        </Stack>
      )}

      {/* Errore della sorgente: dichiarato e non bloccante. Non compare nessun
          valore stimato, perche' nessuna stima e' arrivata. */}
      {sourceError && (
        <Alert
          severity="warning"
          data-testid={`${testIdPrefix}-source-error`}
          sx={{ mt: 1 }}
        >
          {getTranslation(translations, SLA_SUGGESTION_TEXT_KEYS.sourceError)}
        </Alert>
      )}

      {suggestions.map((suggestion) => {
        const applicable = isSlaSuggestionApplicable(suggestion, modelRevision);
        const applied = isApplied?.(suggestion) ?? false;
        return (
          <Stack
            key={slaSuggestionKey(suggestion)}
            spacing={0.5}
            mt={1}
            data-testid={`${testIdPrefix}-item`}
            data-sla-applicable={applicable ? "true" : "false"}
            data-sla-applied={applied ? "true" : "false"}
          >
            {/* Stima non disponibile / cold start: si dichiara, senza inventare
                un istante. `estimable` e' la dichiarazione autorevole. */}
            {!estimateRenderedByCaller &&
              !isSlaSuggestionAvailable(suggestion) && (
              <Alert
                severity="warning"
                data-testid={`${testIdPrefix}-unavailable`}
                sx={{ py: 0 }}
              >
                {getTranslation(translations, SLA_SUGGESTION_UNAVAILABLE_KEY)}
              </Alert>
            )}

            {!estimateRenderedByCaller &&
              isSlaSuggestionAvailable(suggestion) && (
              <Typography
                variant="body2"
                data-testid={`${testIdPrefix}-value`}
              >
                {getTranslation(translations, SLA_START_TEXT_KEYS.previewDeadline)}
                {": "}
                {formatDateTime(suggestion.value as string)}
              </Typography>
            )}

            {!estimateRenderedByCaller && suggestion.asOf && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`${testIdPrefix}-as-of`}
              >
                {getTranslation(translations, SLA_START_TEXT_KEYS.previewAsOf)}
                {": "}
                {formatDateTime(suggestion.asOf)}
              </Typography>
            )}

            {/* Messaggi della sorgente: resi come sono, senza reinterpretarli. */}
            {(suggestion.warnings ?? []).map((warning) => (
              <Typography key={warning} variant="caption" color="text.secondary">
                {warning}
              </Typography>
            ))}

            {applied && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`${testIdPrefix}-applied`}
              >
                {getTranslation(translations, SLA_SUGGESTION_TEXT_KEYS.applied)}
              </Typography>
            )}

            {/* Il comando di conferma esiste solo se la superficie puo' scrivere
                davvero il valore (target di processo e ruolo abilitato) e la
                stima e' applicabile. Nessun pulsante disabilitato-finto: dove
                non si puo' applicare, la proposta resta informativa e il
                criterio "modificabile" non e' soddisfatto (residuo dichiarato
                nel riepilogo di F2c, non simulato con un comando inerte). */}
            {onApply && applicable && !applied && (
              <FlowentiButton
                id={`${testIdPrefix}-apply`}
                size="small"
                variant="outlined"
                sx={{ alignSelf: "flex-start" }}
                onClick={() => onApply(suggestion)}
              >
                {getTranslation(translations, SLA_SUGGESTION_TEXT_KEYS.apply)}
              </FlowentiButton>
            )}
          </Stack>
        );
      })}
    </Box>
  );
};

export default SlaSuggestions;
